/* eslint-disable react/no-danger */
import { arrayOf, bool, func, object, shape, string } from "prop-types";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";

import GroupThumbnail from "web/Pages/Checkout/Subscription/GroupThumbnail";

import isArrayHasItems from "web/utils/data/validator/array/isArrayHasItems";
import getBenefitAttachments from "web/utils/page/product/subscription/getBenefitAttachments";
import BrowserPersistence from "web/utils/system/storage/storage/browserPersistence";

import {
  DUPLICATE_CALL,
  INITIAL_DUPLICATE_RECEIVER_DATA,
} from "web/constants/benefits";
import restUrls from "web/constants/restUrls";
import storageNames from "web/constants/storageNames";

import { request } from "web/api";
import classify from "web/classify";

import BenefitForm from "./BenefitForm";
import BenefitHeader from "./BenefitHeader";
import BenefitUserData from "./BenefitUserData";
import Labels from "./Labels";
import defaultClasses from "./benefitSummary.scss";

const CheckoutSubscriptionBenefitSummary = ({
  extendedView,
  onlyHeader,
  classes,
  formView,
  setModal,
  isMyBenefitsView,
  handleItemUpdate,
  handleToggleEditModal,
  formsEditedByUsers,
  setFormsEditedByUsers,
  groupName,
  items,
  benefitGroup,
  pointsBanks,
  totals,
  duplicateCardDataDesktop,
  showDuplicateForm,
  setShowDuplicateButton,
  handleToggleDuplicateModal,
  updatedItem,
  onFormUpdateSucceeded,
  benefitsWithFormsToUpdate,
  formDuplicateInfoMyBenefitsView,
  setFormDuplicateInfoMyBenefitsView,
  outerFormValues,
}) => {
  const { benefitGroupType } = useSelector((state) => state.subscriptionQuote);
  const [duplicateCardData, setDuplicateCardData] = useState(null);
  const [duplicateReceiverData, setDuplicateReceiverData] = useState(
    INITIAL_DUPLICATE_RECEIVER_DATA
  );
  const [
    showDuplicateFormInformationStep,
    setShowDuplicateFormInformationStep,
  ] = useState(false);

  const storage = new BrowserPersistence();
  const token = storage.getItem(storageNames.tokenAccess);
  useEffect(() => {
    const fetchCardData = async () => {
      try {
        const response = await request(restUrls.duplicateCardConfiguration, {
          headers: {
            accept: "application/ld+json",
            "content-type": "application/ld+json",
            authorization: `Bearer ${token}`,
          },
        });

        setDuplicateCardData(response);
      } catch (errorData) {
        console.error(errorData);
      }
    };
    if (!duplicateCardData && showDuplicateForm === DUPLICATE_CALL.from_stepTwo)
      fetchCardData();
  }, []);

  const beneftiItems = items?.map((item) => {
    const {
      "@id": _id,
      name,
      description,
      quantity,
      pricing,
      forms,
      activeForms,
      benefit,
      benefitEmployeeGroupPrice,
      id: benefitId,
      endsAt,
      employerPriceAlternativeText,
      status,
      benefitGroupType: benefitGroupTypeItem,
    } = item;

    const {
      relationBenefitFreeTypeConfiguration,
      relationBenefitTypeConfiguration,
    } = benefit;

    const benefitFormsToUpdate = benefitsWithFormsToUpdate.find(
      ({ benefitId: bId }) => bId === _id
    );

    // eslint-disable-next-line no-underscore-dangle
    const outerFormBenefitValues = outerFormValues?.[_id];

    const eachBenefitData =
      typeof benefitEmployeeGroupPrice === "object"
        ? benefitEmployeeGroupPrice
        : item;
    const attachments = getBenefitAttachments(benefit);

    const currentBenefitTotals = totals?.items.filter((ele) =>
      benefit["@id"].includes(ele.benefitId)
    );

    const totalsPrices = isArrayHasItems(currentBenefitTotals)
      ? currentBenefitTotals[0]
      : [];

    const headerAndBenefitData = (
      <>
        <BenefitHeader
          name={name}
          description={description}
          quantity={quantity}
          extendedView={extendedView}
          employerPriceType={pricing?.currentPriceType?.employerPriceType}
          employerPrice={totalsPrices?.employerPrice}
          employerPriceAlternativeText={
            pricing?.currentPriceType?.employerPriceAlternativeText ||
            employerPriceAlternativeText
          }
          employeePrice={totalsPrices?.employeePrice}
          attachments={attachments}
          setModal={setModal}
          relationBenefitTypeConfiguration={relationBenefitTypeConfiguration}
          relationBenefitFreeTypeConfiguration={
            relationBenefitFreeTypeConfiguration
          }
          benefitDraftUUID={benefit["@id"]}
          benefitEmployeeGroupPrice={eachBenefitData}
          benefitGroupType={benefitGroupType}
          pointsBanks={pointsBanks}
          totals={totals}
          groupName={groupName}
        />
        {!onlyHeader && (
          <BenefitUserData
            duplicateCardData={duplicateCardDataDesktop}
            forms={forms}
            activeForms={activeForms}
            setModal={setModal}
            wayChooseOthersInActiveForm={benefit.wayChooseOthersInActiveForm}
            setShowDuplicateButton={setShowDuplicateButton}
            showDuplicateForm={showDuplicateForm}
            handleToggleDuplicateModal={handleToggleDuplicateModal}
            updatedItem={updatedItem}
            formsEditedByUsers={formsEditedByUsers}
            formDuplicateInfoMyBenefitsView={formDuplicateInfoMyBenefitsView}
            setFormDuplicateInfoMyBenefitsView={
              setFormDuplicateInfoMyBenefitsView
            }
          />
        )}
      </>
    );

    const duplicateCardDataObject = {
      duplicateCardData,
      showDuplicateForm,
      setShowDuplicateFormInformationStep,
      showDuplicateFormInformationStep,
    };

    return (
      <div className={defaultClasses.itemWrapper} key={benefitId}>
        <div
          className={classes.benefit}
          data-t1="subscription_name"
          data-t2={name}
        >
          {formView ? (
            <>
              <BenefitHeader
                name={name}
                quantity={quantity}
                extendedView={extendedView}
                employerPrice={totalsPrices?.employerPrice}
                employerPriceAlternativeText={
                  pricing?.currentPriceType?.employerPriceAlternativeText ||
                  employerPriceAlternativeText
                }
                benefitDraftUUID={benefit["@id"]}
                employeePrice={totalsPrices?.employeePrice}
                benefitEmployeeGroupPrice={eachBenefitData}
                benefitGroupType={benefitGroupType}
                pointsBanks={pointsBanks}
                endsAt={endsAt}
                groupName={groupName}
              />
              <BenefitForm
                forms={forms}
                activeForms={activeForms}
                setModal={setModal}
                benefitId={_id}
                benefitUUID={benefitId}
                isMyBenefitsView={isMyBenefitsView}
                handleItemUpdate={handleItemUpdate}
                handleToggleEditModal={handleToggleEditModal}
                formsEditedByUsers={formsEditedByUsers}
                setFormsEditedByUsers={setFormsEditedByUsers}
                wayChooseOthersInActiveForm={
                  benefit.wayChooseOthersInActiveForm
                }
                duplicateCardDataObject={duplicateCardDataObject}
                setDuplicateReceiverData={setDuplicateReceiverData}
                duplicateReceiverData={duplicateReceiverData}
                onFormUpdateSucceeded={onFormUpdateSucceeded}
                formsToUpdate={benefitFormsToUpdate}
                enableOrderDuplicateCard={benefit.enableOrderDuplicateCard}
                benefitStatus={status}
                outerFormValues={outerFormBenefitValues}
                benefitGroupTypeItem={benefitGroupTypeItem}
              />
            </>
          ) : (
            headerAndBenefitData
          )}
        </div>
      </div>
    );
  });

  // One condition for step 2 summary view, second condition for desktop summary view
  const ifDisyplayPhoto =
    benefitGroupType === "2" ||
    items[0]?.benefit?.benefitGroup?.groupType === 2;
  const classesHeader = ifDisyplayPhoto ? classes.headerUMS : classes.header;
  const groupPhoto = ifDisyplayPhoto && (
    <GroupThumbnail
      src={
        benefitGroup?.groupPhoto?.contentUrl ||
        items[0].benefit?.benefitGroup?.groupPhoto?.contentUrl
      }
      alt={groupName}
    />
  );

  return (
    <fieldset className={classes.root}>
      <header className={classesHeader}>
        {groupPhoto}
        <h3 className={classes.header__title}>{groupName}</h3>
      </header>
      <section className={classes.body}>
        <Labels
          extendedView={extendedView}
          benefitGroupType={benefitGroupType}
        />
        {beneftiItems}
      </section>
    </fieldset>
  );
};
CheckoutSubscriptionBenefitSummary.propTypes = {
  classes: shape({}).isRequired,
  groupName: string,
  extendedView: bool,
  formView: bool,
  setModal: func,
  isMyBenefitsView: bool,
  handleItemUpdate: func,
  handleToggleEditModal: func,
  formsEditedByUsers: arrayOf(string),
  setFormsEditedByUsers: func,
  onlyHeader: bool,
  items: arrayOf(shape({})).isRequired,
  benefitGroup: shape({}),
  pointsBanks: arrayOf(shape({})),
  totals: shape({}).isRequired,
  duplicateCardDataDesktop: shape({}),
  setShowDuplicateButton: func,
  showDuplicateForm: string,
  benefitsWithFormsToUpdate: arrayOf(shape({})),
  onFormUpdateSucceeded: func,
  outerFormValues: object,
};
CheckoutSubscriptionBenefitSummary.defaultProps = {
  groupName: "",
  extendedView: false,
  formView: false,
  setModal: () => {},
  onlyHeader: false,
  isMyBenefitsView: false,
  handleItemUpdate: null,
  handleToggleEditModal: null,
  formsEditedByUsers: [],
  setFormsEditedByUsers: () => {},
  benefitGroup: null,
  pointsBanks: [],
  duplicateCardDataDesktop: null,
  setShowDuplicateButton: null,
  showDuplicateForm: null,
  benefitsWithFormsToUpdate: [],
  onFormUpdateSucceeded: () => {},
  outerFormValues: null,
};

export default classify(defaultClasses)(CheckoutSubscriptionBenefitSummary);
